/* DEBACQ newsletter enhancement. Extends the theme's existing popup class.
 * Does not modify the theme's classes, global styles, forms or app settings.
 * Native Shopify POST is intentional: CAPTCHA and subscription processing
 * stay with Shopify. An email-shaped string alone is NEVER a success.
 */
import { NewsletterPopup, DialogElement } from "theme";

const REVEAL_THRESHOLD = 0.40;
const BRUSH_WIDTH_RATIO = 0.12;
const PENDING_LIFETIME = 30 * 60 * 1000;
const STEPS = ["scratch", "mystery", "email", "confirmation"];

function storageRead(kind, key) {
  try { return window[kind].getItem(key); } catch (_) { return null; }
}
function storageWrite(kind, key, value) {
  try { window[kind].setItem(key, value); } catch (_) { /* Storage can be blocked. */ }
}
function storageRemove(kind, key) {
  try { window[kind].removeItem(key); } catch (_) { /* Storage can be blocked. */ }
}

class DebacqNewsletterPopup extends NewsletterPopup {
  // The theme's normal PopIn does not trap focus or lock scroll. Its inherited
  // DialogElement already provides both, which are needed for this full screen.
  get shouldTrapFocus() { return true; }
  get shouldLock() { return true; }
  get designMode() { return Boolean(window.Shopify?.designMode); }

  // Own this instance's timer so editor unloads/close can cancel it. The existing
  // delay and suppression keys are still used, via the superclass getter below.
  get shouldAppearAutomatically() { return false; }

  connectedCallback() {
    super.connectedCallback();
    this._confirmed = false;
    this._step = "";
    this._pointer = null;
    this._previous = null;
    this._moveCount = 0;
    this._canvasReady = false;
    this._pendingKey = `debacq:newsletter-pending:${this.dataset.formId}`;
    this._panel = this.querySelector("[data-dbq-panel]");
    this._journey = this.querySelector("[data-dbq-journey]");
    this._emailScreen = this.querySelector("[data-dbq-email]");
    this._confirmation = this.querySelector("[data-dbq-confirmation]");
    this._card = this.querySelector("[data-dbq-card]");
    this._canvas = this.querySelector("[data-dbq-canvas]");
    this._cover = this.querySelector("[data-dbq-cover]");
    this._label = this.querySelector("[data-dbq-label]");
    this._underlay = this.querySelector("[data-dbq-underlay]");
    this._rewardTitle = this.querySelector("[data-dbq-reward-title]");
    this._claim = this.querySelector("[data-dbq-claim]");
    this._form = this.querySelector("[data-dbq-newsletter-form]");
    this._returnLink = this.querySelector("[data-dbq-return] a");
    this._cardAccessibleLabel = this._card.getAttribute("aria-label");
    try { this._context = this._canvas.getContext("2d", { willReadFrequently: true }); }
    catch (_) { this._context = null; }

    const pending = this.readPending();
    this._returnPath = this.localPath(this.dataset.redirect || pending?.path || this.currentPath());
    this.setDiscountLink();
    this.setStep(this.designMode ? this.dataset.editorStep : "scratch");

    const options = { signal: this.abortController.signal };
    this._claim.addEventListener("click", () => this.setStep("email", true), options);
    this._card.addEventListener("pointerdown", this.onPointerDown.bind(this), options);
    this._card.addEventListener("pointermove", this.onPointerMove.bind(this), options);
    this._card.addEventListener("pointerup", this.onPointerUp.bind(this), options);
    this._card.addEventListener("pointercancel", this.stopPointer.bind(this), options);
    this._card.addEventListener("lostpointercapture", () => { this._pointer = null; this._previous = null; }, options);
    this._card.addEventListener("keydown", event => {
      if (this._step === "scratch" && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        this.reveal();
      }
    }, options);
    this._form.addEventListener("submit", this.onSubmit.bind(this), options);
    this._returnLink.addEventListener("click", event => {
      if (this.designMode || !this._confirmed) {
        event.preventDefault();
        if (this.designMode) this.hide();
      }
    }, options);
    this.addEventListener("dialog:before-show", () => {
      this.querySelectorAll("img").forEach(image => { image.loading = "eager"; });
      this.paintCover();
    }, options);
    this.addEventListener("dialog:after-show", () => this.paintCover(), options);
    this.addEventListener("dialog:before-hide", () => {
      clearTimeout(this._autoTimer);
      clearTimeout(this._focusTimer);
      this.stopPointer();
    }, options);

    // The theme editor replaces this section's DOM without reloading the page.
    // Custom-element lifecycle and inherited editor events handle the replacement.
    if (this.designMode && this._shopifySection) {
      this._shopifySection.addEventListener("shopify:section:load", () => this.show(false), options);
    }
    if (typeof ResizeObserver !== "undefined") {
      this._resizeObserver = new ResizeObserver(() => this.paintCover());
      this._resizeObserver.observe(this._card);
    } else {
      window.addEventListener("resize", () => this.paintCover(), options);
    }
    Promise.resolve(document.fonts?.ready).then(() => {
      if (!this.isConnected) return;
      this._fontsReady = true;
      this.paintCover();
    });

    if (this.designMode) return;

    // Shopify's form result can be present on multiple newsletter forms on the
    // same page. Require this form's anchor or a recent, same-page attempt.
    // Pending storage contains only timestamp/path, never an email address.
    const hash = location.hash;
    const ownAnchor = hash === `#${this.dataset.formId}`;
    const pendingHere = pending && this.localPath(pending.path).split("?")[0].split("#")[0] === location.pathname;
    const ownsResponse = ownAnchor || (pendingHere && !hash);
    const result = this.dataset.formResult;
    if (ownsResponse && result === "success") {
      this._confirmed = true;
      storageWrite("localStorage", "theme:popup-filled", "true");
      storageRemove("sessionStorage", this._pendingKey);
      this.setStep("confirmation");
      this.cleanResponseURL();
      this.show(false);
    } else if (ownsResponse && result === "error") {
      storageRemove("sessionStorage", this._pendingKey);
      this.setStep("email");
      this.show(false);
    } else {
      // A missing/ambiguous server result is not promoted to a success state.
      let allowed = true;
      try { allowed = super.shouldAppearAutomatically; } catch (_) { /* Blocked storage. */ }
      if (allowed) this._autoTimer = setTimeout(() => this.showAutomatically(), this.apparitionDelay);
    }
  }

  disconnectedCallback() {
    clearTimeout(this._autoTimer);
    clearTimeout(this._focusTimer);
    this.stopPointer();
    this._resizeObserver?.disconnect();
    super.disconnectedCallback();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    // Exactly the same dialog behavior as the theme, with storage made safe in
    // private/restricted browsing and no suppression writes in the editor.
    DialogElement.prototype.attributeChangedCallback.call(this, name, oldValue, newValue);
    if (name === "open" && this.open && !this.designMode) {
      storageWrite("localStorage", "theme:popup-appeared", "true");
    }
  }

  showAutomatically() {
    this._autoTimer = null;
    if (!this.isConnected || this.open || this.designMode || document.hidden) return;
    // Recheck suppression at show time, including a signup in another tab.
    try { if (!super.shouldAppearAutomatically) return; } catch (_) { /* Blocked storage. */ }
    // Do not interrupt a cart, image viewer or another dialog. Skip this page's
    // automatic attempt rather than installing a polling loop. No "seen" flag is
    // written when skipped; a later eligible page can still present the signup.
    if (document.documentElement.classList.contains("lock") ||
        document.querySelector('x-modal[open], x-drawer[open], cart-drawer[open], dialog[open], .pswp--open')) return;
    const cartHeader = document.querySelector('[data-essential-cart-element="cart-drawer-header"]');
    if (cartHeader) {
      const box = cartHeader.getBoundingClientRect();
      let visible = box.width > 0 && box.height > 0 && box.right > 0 &&
        box.left < innerWidth && box.bottom > 0 && box.top < innerHeight;
      for (let node = cartHeader; visible && node instanceof Element; node = node.parentElement) {
        const style = getComputedStyle(node);
        visible = !node.hidden && node.getAttribute("aria-hidden") !== "true" &&
          style.display !== "none" && style.visibility !== "hidden" && Number(style.opacity) !== 0;
      }
      if (visible) return;
    }
    return this.show();
  }

  show(animate = true) {
    if (!this.isConnected) return Promise.resolve();
    return super.show(animate);
  }

  hide() {
    const applyDiscount = this.open && this._confirmed && !this.designMode;
    const result = super.hide();
    if (applyDiscount) window.location.assign(this._returnLink.href);
    return result;
  }

  readPending() {
    try {
      const record = JSON.parse(storageRead("sessionStorage", this._pendingKey));
      if (record && typeof record.path === "string" && Number.isFinite(record.at)
          && Date.now() >= record.at && Date.now() - record.at < PENDING_LIFETIME) return record;
    } catch (_) { /* Missing or malformed storage is ignored. */ }
    return null;
  }

  localPath(value) {
    const fallback = this.dataset.rootUrl || "/";
    if (typeof value !== "string" || /[\\\u0000-\u001f\u007f]/.test(value) || value.startsWith("//")) return fallback;
    try {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || !/^https?:$/.test(url.protocol)) return fallback;
      if (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(?:discount|checkout|checkouts|account|challenge)(?:\/|$)/i.test(url.pathname)) return fallback;
      ["customer_posted", "contact_posted", "form_type"].forEach(key => url.searchParams.delete(key));
      if (url.hash === `#${this.dataset.formId}`) url.hash = "";
      return url.pathname + url.search + url.hash;
    } catch (_) { return fallback; }
  }

  currentPath() { return this.localPath(location.pathname + location.search + location.hash); }

  setDiscountLink() {
    const code = this.dataset.discountCode?.trim();
    if (!code) {
      this._returnLink.href = this._returnPath;
      return;
    }
    // Visit Shopify's discount endpoint, not just a cosmetic query string.
    // Also include the code in the landing URL so it is visible on return.
    // Keep the existing product/collection, query parameters and page anchor.
    const destination = new URL(this._returnPath, location.origin);
    destination.searchParams.set("discount", code);
    const root = (this.dataset.rootUrl || "/").replace(/\/?$/, "/");
    const url = new URL(`${root}discount/${encodeURIComponent(code)}`, location.origin);
    url.searchParams.set("redirect", destination.pathname + destination.search + destination.hash);
    this._returnLink.href = url.href;
  }

  cleanResponseURL() {
    try { history.replaceState(history.state, "", this.currentPath()); }
    catch (_) { /* Cleanup is cosmetic, not a signup requirement. */ }
  }

  onSubmit(event) {
    if (this.designMode) {
      event.preventDefault();
      // Preview only. Never make a customer record or apply a discount here.
      this.setStep("confirmation", true);
      return;
    }
    if (!this._form.checkValidity()) return;
    storageWrite("sessionStorage", this._pendingKey, JSON.stringify({ at: Date.now(), path: this.currentPath() }));
    // Do not preventDefault(), use fetch(), call form.submit(), disable the
    // button, or set a success state. Leave Shopify's CAPTCHA/native POST intact.
  }

  setStep(step, focus = false) {
    if (!STEPS.includes(step)) step = "scratch";
    const previousStep = this._step;
    this._step = step;
    this.dataset.step = step;
    this.stopPointer();
    clearTimeout(this._focusTimer);
    const journey = step === "scratch" || step === "mystery";
    this._journey.hidden = !journey;
    this._emailScreen.hidden = step !== "email";
    this._confirmation.hidden = step !== "confirmation";
    this._underlay.setAttribute("aria-hidden", String(step === "scratch"));
    this._claim.setAttribute("aria-hidden", String(step !== "mystery"));
    this._claim.querySelector("button").tabIndex = step === "mystery" ? 0 : -1;
    this._card.tabIndex = step === "scratch" ? 0 : -1;
    if (step === "scratch") {
      this._card.setAttribute("role", "button");
      this._card.setAttribute("aria-label", this._cardAccessibleLabel);
    } else {
      this._card.removeAttribute("role");
      this._card.removeAttribute("aria-label");
    }
    const headingSuffix = step === "mystery" ? "reward" : step;
    this.setAttribute("aria-labelledby", `${this.id}-${headingSuffix}-title`);
    // Scratch -> mystery leaves the same DOM node and all reserved geometry.
    if (!(previousStep === "scratch" && step === "mystery")) this._panel.scrollTop = 0;
    if (step === "scratch") this.paintCover();
    if (focus) {
      this._focusTimer = setTimeout(() => {
        if (!this.isConnected || !this.open) return;
        const target = step === "scratch" ? this._card : this.querySelector(`#${CSS.escape(this.id + "-" + headingSuffix + "-title")}`);
        target?.focus({ preventScroll: true });
      }, step === "mystery" && !matchMedia("(prefers-reduced-motion: reduce)").matches ? 230 : 0);
    }
  }

  paintCover() {
    if (!this._context || !this._fontsReady || this._step !== "scratch") return;
    const rect = this._card.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.round(rect.width * dpr), height = Math.round(rect.height * dpr);
    if (this._canvasReady && width === this._canvas.width && height === this._canvas.height) return;
    const ctx = this._context;
    let oldCanvas = null;
    // Preserve already-scratched coverage on resize/rotation instead of resetting.
    if (this._canvasReady) {
      oldCanvas = document.createElement("canvas");
      oldCanvas.width = this._canvas.width; oldCanvas.height = this._canvas.height;
      oldCanvas.getContext("2d").drawImage(this._canvas, 0, 0);
    }
    this._canvas.width = width; this._canvas.height = height;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalCompositeOperation = "source-over";
    if (oldCanvas) {
      ctx.drawImage(oldCanvas, 0, 0, rect.width, rect.height);
    } else {
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, rect.width, rect.height);
      ctx.strokeStyle = "rgba(255,255,255,.65)";
      ctx.lineWidth = 1;
      ctx.strokeRect(.5, .5, rect.width - 1, rect.height - 1);
      const style = getComputedStyle(this._label);
      const size = parseFloat(style.fontSize);
      ctx.font = `${style.fontStyle} ${style.fontWeight} ${size}px ${style.fontFamily}`;
      ctx.fillStyle = "#fff";
      ctx.textBaseline = "middle";
      let text = this._label.textContent.trim();
      if (style.textTransform === "uppercase") text = text.toLocaleUpperCase();
      if (style.textTransform === "lowercase") text = text.toLocaleLowerCase();
      const spacing = parseFloat(style.letterSpacing) || 0;
      const glyphs = Array.from(text);
      const widths = glyphs.map(glyph => ctx.measureText(glyph).width);
      const total = widths.reduce((sum, value) => sum + value, 0) + spacing * Math.max(0, glyphs.length - 1);
      const scale = Math.min(1, rect.width * .88 / Math.max(total, 1));
      ctx.save();
      ctx.translate(rect.width / 2, rect.height / 2);
      ctx.scale(scale, scale);
      let x = -total / 2;
      ctx.textAlign = "left";
      glyphs.forEach((glyph, index) => { ctx.fillText(glyph, x, 0); x += widths[index] + spacing; });
      ctx.restore();
      this._card.dataset.scratchProgress = "0";
    }
    this._canvasReady = true;
    this._card.setAttribute("data-canvas-ready", "");
  }

  point(event) {
    const rect = this._card.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  erase(point) {
    const ctx = this._context;
    const brush = this._card.getBoundingClientRect().width * BRUSH_WIDTH_RATIO;
    ctx.globalCompositeOperation = "destination-out";
    ctx.lineWidth = brush;
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(this._previous ? this._previous.x : point.x, this._previous ? this._previous.y : point.y);
    ctx.lineTo(point.x, point.y); ctx.stroke();
    ctx.beginPath(); ctx.arc(point.x, point.y, brush / 2, 0, Math.PI * 2); ctx.fill();
    this._previous = point;
  }

  checkCompletion() {
    if (this._step !== "scratch" || !this._context || !this._canvasReady) return;
    const { width, height } = this._canvas;
    const data = this._context.getImageData(0, 0, width, height).data;
    let remainingAlpha = 0;
    for (let index = 3; index < data.length; index += 4) remainingAlpha += data[index];
    const erased = 1 - remainingAlpha / (255 * width * height);
    this._card.dataset.scratchProgress = erased.toFixed(6);
    if (erased >= REVEAL_THRESHOLD) this.reveal();
  }

  onPointerDown(event) {
    if (this._step !== "scratch" || event.button !== 0 || this._pointer !== null) return;
    if (!this._context) { this.reveal(); return; }
    if (!this._canvasReady) return;
    event.preventDefault();
    this._pointer = event.pointerId;
    this._previous = null;
    this._moveCount = 0;
    try { this._card.setPointerCapture(event.pointerId); } catch (_) { /* Pointer may have ended. */ }
    this.erase(this.point(event));
  }

  onPointerMove(event) {
    if (this._step !== "scratch" || event.pointerId !== this._pointer) return;
    event.preventDefault();
    this.erase(this.point(event));
    if (++this._moveCount % 6 === 0) this.checkCompletion();
  }

  onPointerUp(event) {
    if (event.pointerId !== this._pointer) return;
    this.stopPointer();
    this.checkCompletion();
  }

  stopPointer() {
    if (this._pointer !== null && this._pointer !== undefined && this._card) {
      try {
        if (this._card.hasPointerCapture(this._pointer)) this._card.releasePointerCapture(this._pointer);
      } catch (_) { /* A removed card may have already released the pointer. */ }
    }
    this._pointer = null;
    this._previous = null;
  }

  reveal() {
    if (this._step === "scratch") this.setStep("mystery", true);
  }
}

if (!customElements.get("debacq-newsletter-popup")) {
  customElements.define("debacq-newsletter-popup", DebacqNewsletterPopup);
}
